import type { TimelineLevel } from '../../../../common/api/loyalty/externalLoyaltyModels';

import { useIsMobile } from '../../../../common/components/atoms/ResponsiveBreakpoints';

type UseGetBonusLevelsResult = {
    bonusLevels: TimelineLevel[];
    progressWidth: number;
    dots: number[];
    haveCompletedLevels: boolean;
};

const getBonusProgress = (bonusLevels: TimelineLevel[]) => {
    const visibleItems = bonusLevels;
    const total = visibleItems.length;
    const dotCount = Math.max(total - 1, 1);

    const getDotPosition = (index: number): number =>
        total === 1 ? 50 : 10 + (index / dotCount) * 80;

    const dots = visibleItems.map((_, index) => getDotPosition(index));

    let currentLevelIndex = visibleItems.findIndex((item) => item.current);

    if (currentLevelIndex === -1) {
        currentLevelIndex = visibleItems.findIndex((item) => !item.completed);
    }

    const current = visibleItems[currentLevelIndex];
    const range = current.toAmount - current.fromAmount;

    const ratio =
        range <= 0
            ? 0
            : Math.max(0, Math.min(1, (current.amountCompleted - current.fromAmount) / range));

    let progressWidth: number;

    if (currentLevelIndex === 0) {
        const firstDot = getDotPosition(0);
        progressWidth =
            current.order === 0 ? 1 + ratio * (firstDot - 1) : 10 + ratio * (firstDot - 10);
    } else {
        const prevDot = getDotPosition(currentLevelIndex - 1);
        const currentDot = getDotPosition(currentLevelIndex);
        progressWidth =
            current.order === 24 && current.completed
                ? 100
                : prevDot + ratio * (currentDot - prevDot);
    }

    return { progressWidth, dots };
};

export const useGetBonusLevels = (levels: TimelineLevel[]): UseGetBonusLevelsResult => {
    const isMobile = useIsMobile();
    const currentIndex = levels.findIndex((level) => level.current);
    const wantedCount = levels[currentIndex].order <= 1 && isMobile ? 3 : 4;
    const haveCompleteAllLevels = levels.every((level) => level.completed);
    const completeAllLevelsCount = haveCompleteAllLevels ? 2 : 3;
    const previousItemsCount = currentIndex >= 2 ? 2 : 1;

    let start = Math.max(currentIndex - previousItemsCount, 0);
    let end = start + wantedCount;

    if (end > levels.length) {
        end = levels.length;
        start = Math.max(end - completeAllLevelsCount, 0);
    }

    const bonusLevels = levels.slice(start, end);

    const haveCompletedLevels = bonusLevels.some((level) => level.completed);
    const { progressWidth, dots } = getBonusProgress(bonusLevels);

    return {
        bonusLevels,
        progressWidth,
        dots,
        haveCompletedLevels,
    };
};
